import {
  Button,
  InlineLoading,
  InlineNotification,
  StructuredListBody,
  StructuredListCell,
  StructuredListHead,
  StructuredListRow,
  StructuredListWrapper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TextArea,
} from '@carbon/react';
import type { FieldEditorProps } from '@pos/admin-ui';
import { ConfirmDialog, PrintPreview, type PrintPreviewLine } from '@pos/design-system';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { hoApi } from '../api';
import './templates.scss';

/** The documents stores print with a template (FR-CFG-12). */
const PRINTABLE = ['receipt', 'bill', 'kitchen_ticket', 'shift_report'] as const;

const TAGS = ['center', 'right', 'b', 'double', 'rule', 'feed', 'cut', 'drawer', 'qr'] as const;
const FUNCTIONS = ['money', 'qty', 'time', 'row', 'cols', 'rule', 'pad', 't'] as const;
const FUNCTION_SAMPLES: Record<(typeof FUNCTIONS)[number], string> = {
  money: '{{ money total }}',
  qty: '{{ qty l.quantity }}',
  time: '{{ time printed_at "HH:mm" }}',
  row: '{{ row "Tổng" (money total) }}',
  cols: '{{ cols [l.name, (money l.line_total)] [30, -18] }}',
  rule: '{{ rule "=" }}',
  pad: '{{ pad_left (qty l.quantity) 4 }}',
  t: '{{ t "Tổng" "Total" }}',
};
/** What each document's lists are called, for the "for … in …" hint. */
const LOOPS: Record<string, string> = {
  receipt: 'for l in bill.lines',
  bill: 'for l in lines',
  kitchen_ticket: 'for i in items',
  shift_report: 'for x in tenders',
};

interface Sample {
  body: string;
  fields?: unknown;
}

interface FieldRow {
  path: string;
  example: string;
}

/** The sample document as rows of field paths and example values; a list shows its first entry as `list[].field`. */
function fieldRows(node: unknown, path = ''): FieldRow[] {
  if (Array.isArray(node)) return node.length ? fieldRows(node[0], `${path}[]`) : [{ path, example: '[]' }];
  if (node !== null && typeof node === 'object') {
    return Object.entries(node as Record<string, unknown>).flatMap(([key, value]) =>
      fieldRows(value, path ? `${path}.${key}` : key),
    );
  }
  return [{ path, example: node === null || node === undefined ? '—' : String(node) }];
}

/** Waits until the value has stopped changing for a moment, so typing doesn't ask for a preview on every key. */
function useSettled<T>(value: T, ms = 400): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * A print template's text (FR-CFG-12, ADR 0047) beside a live preview: the template laid out on a made-up document by the
 * same code a store prints with, at the template's paper width. The built-in layout is one click away to start from, and the
 * fields, tags and functions are listed next to it.
 */
export function TemplateBodyEditor({ field, value, invalidText, values, onChange }: FieldEditorProps) {
  const { t } = useTranslation();
  const body = typeof value === 'string' ? value : '';
  const documentType = typeof values.documentType === 'string' ? values.documentType : '';
  const printable = (PRINTABLE as readonly string[]).includes(documentType);
  const footerOnly = values.engine === 'text';
  const paperWidthMm = Number(values.paperWidthMm) === 58 ? 58 : 80;
  const language = values.language === 'en' ? 'en' : 'vi';
  const documentName = documentType ? t(`md.enums.document_type.${documentType}` as I18nKey) : '';
  const [confirming, setConfirming] = useState(false);

  const sample = useQuery({
    queryKey: ['ho', 'print-template-sample', documentType],
    enabled: printable,
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/devices/print-templates/samples/{documentType}', {
        params: { path: { documentType } },
      });
      if (!data) throw error;
      return data as Sample;
    },
  });

  const request = useSettled({ documentType, body, paperWidthMm, language });
  const preview = useQuery({
    queryKey: ['ho', 'print-template-preview', request],
    enabled: printable && !footerOnly && request.body.trim().length > 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/devices/print-templates/preview', { body: request });
      if (!data) throw error;
      return data;
    },
  });

  const startFromBuiltIn = () => {
    if (sample.data) onChange(sample.data.body);
    setConfirming(false);
  };
  const error = preview.data?.error;

  return (
    <div className="ho-template-editor">
      <div className="ho-template-editor__text">
        <TextArea
          id="template-body"
          className="ho-template-editor__body"
          labelText={t(field.label as I18nKey)}
          value={body}
          rows={28}
          invalid={Boolean(invalidText)}
          invalidText={invalidText}
          onChange={(event) => onChange(event.target.value)}
        />
        {printable && !footerOnly && (
          <Button
            kind="tertiary"
            disabled={!sample.data}
            onClick={() => (body.trim() ? setConfirming(true) : startFromBuiltIn())}
          >
            {t('printTemplates.startFromBuiltIn')}
          </Button>
        )}
        <ConfirmDialog
          open={confirming}
          title={t('printTemplates.replaceTitle')}
          confirmLabel={t('printTemplates.replace')}
          onConfirm={startFromBuiltIn}
          onCancel={() => setConfirming(false)}
        >
          {t('printTemplates.replaceBody')}
        </ConfirmDialog>
      </div>

      <div className="ho-template-editor__side">
        {footerOnly ? (
          <InlineNotification kind="info" lowContrast hideCloseButton title={t('printTemplates.footerOnly')} />
        ) : !printable ? (
          <InlineNotification kind="info" lowContrast hideCloseButton title={t('printTemplates.chooseType')} />
        ) : (
          <Tabs>
            <TabList aria-label={t('printTemplates.preview')} contained>
              <Tab>{t('printTemplates.preview')}</Tab>
              <Tab>{t('printTemplates.fields')}</Tab>
              <Tab>{t('printTemplates.help')}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <p className="ho-template-editor__hint">
                  {t('printTemplates.previewHint', { document: documentName, width: paperWidthMm })}
                </p>
                {preview.isFetching && !preview.data && <InlineLoading />}
                {preview.isError && (
                  <InlineNotification
                    kind="error"
                    lowContrast
                    hideCloseButton
                    title={t('printTemplates.previewFailed')}
                  />
                )}
                {error && (
                  <InlineNotification
                    kind="error"
                    lowContrast
                    hideCloseButton
                    title={
                      Number(error.line) > 0
                        ? t('printTemplates.previewError', {
                            line: Number(error.line),
                            column: Number(error.column),
                            message: error.message,
                          })
                        : t('printTemplates.previewErrorNoLine', { message: error.message })
                    }
                  />
                )}
                {preview.data && !error && (
                  <PrintPreview
                    label={t('printTemplates.preview')}
                    columns={Number(preview.data.columns)}
                    lines={preview.data.lines as PrintPreviewLine[]}
                  />
                )}
              </TabPanel>
              <TabPanel>
                <p className="ho-template-editor__hint">
                  {t('printTemplates.fieldsHint', { document: documentName, loop: LOOPS[documentType] ?? '' })}
                </p>
                <StructuredListWrapper isCondensed aria-label={t('printTemplates.fields')}>
                  <StructuredListHead>
                    <StructuredListRow head>
                      <StructuredListCell head>{t('printTemplates.field')}</StructuredListCell>
                      <StructuredListCell head>{t('printTemplates.example')}</StructuredListCell>
                    </StructuredListRow>
                  </StructuredListHead>
                  <StructuredListBody>
                    {fieldRows(sample.data?.fields).map((row) => (
                      <StructuredListRow key={row.path}>
                        <StructuredListCell>
                          <code>{row.path}</code>
                        </StructuredListCell>
                        <StructuredListCell>{row.example}</StructuredListCell>
                      </StructuredListRow>
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
              </TabPanel>
              <TabPanel>
                <p className="ho-template-editor__hint">{t('printTemplates.helpIntro')}</p>
                <StructuredListWrapper isCondensed aria-label={t('printTemplates.help')}>
                  <StructuredListHead>
                    <StructuredListRow head>
                      <StructuredListCell head>{t('printTemplates.tags')}</StructuredListCell>
                      <StructuredListCell head />
                    </StructuredListRow>
                  </StructuredListHead>
                  <StructuredListBody>
                    {TAGS.map((tag) => (
                      <StructuredListRow key={tag}>
                        <StructuredListCell>
                          <code>{`<${tag}>`}</code>
                        </StructuredListCell>
                        <StructuredListCell>{t(`printTemplates.tag.${tag}` as I18nKey)}</StructuredListCell>
                      </StructuredListRow>
                    ))}
                    {FUNCTIONS.map((name) => (
                      <StructuredListRow key={`fn-${name}`}>
                        <StructuredListCell>
                          <code>{FUNCTION_SAMPLES[name]}</code>
                        </StructuredListCell>
                        <StructuredListCell>{t(`printTemplates.function.${name}` as I18nKey)}</StructuredListCell>
                      </StructuredListRow>
                    ))}
                    <StructuredListRow>
                      <StructuredListCell>
                        <code>{'{{ paper.columns }}'}</code>
                      </StructuredListCell>
                      <StructuredListCell>{t('printTemplates.paper.columns')}</StructuredListCell>
                    </StructuredListRow>
                  </StructuredListBody>
                </StructuredListWrapper>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </div>
    </div>
  );
}
